import BadgeIcon from '@mui/icons-material/Badge';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ConstructionIcon from '@mui/icons-material/Construction';
import HistoryIcon from '@mui/icons-material/History';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import LockIcon from '@mui/icons-material/Lock';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import type { Route } from './+types/domains_.$domain';

import { Paid } from '@mui/icons-material';
import {
  alpha,
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { isAxiosError, type AxiosError } from 'axios';
import { useMemo } from 'react';
import { data } from 'react-router';
import { ApiClient } from '~/api/Endpoints';
import { Button } from '~/components/Button';
import { DomainNoResults } from '~/components/DomainNoResults';
import { DomainTechnologiesTable } from '~/components/DomainTechnologiesTable';
import { FaviconImage } from '~/components/FaviconImage';
import { Link } from '~/components/Link';
import { Breadcrumb, PageTemplate } from '~/components/PageTemplate';
import { BlurWrapper } from '~/components/SearchResultsTable/BlurWrapper';
import { SocialLink } from '~/components/SocialLink';
import useCookieStorage from '~/hooks/useCookieStorage';
import { useSuggestions } from '~/hooks/useSuggestions';
import { useUser } from '~/hooks/useUser';
import { DOMAIN_NOT_FOUND_ERROR_CODE } from '@shared/constants';
import { deserializeAxiosError, serializeAxiosError } from '~/lib/axios';
import { isLoggedIn } from '~/loaders/isLoggedIn';
import { DomainSearchForm } from '~/scenes/domains/DomainSearchForm';
import { IntegrationsCards } from '~/scenes/integrations/IntegrationCards';
import {
  EXTENSION_INSTALLED_COOKIE_NAME,
  SEARCH_FILTER_UPDATED_AT_MONTHS,
} from '~/scripts/constants';
import { countries } from '~/scripts/countries';
import { getPageMetaTags } from '~/scripts/getPageMetaTags';
import { getRankTier } from '~/scripts/getRankTier';
import { numberAbbreviation } from '~/scripts/numberAbbreviation';
import parseDomain from '~/scripts/parseDomain';
import { ucfirst } from '~/scripts/ucfirst';
import { Domains } from '~/types/domains';
import type { Search } from '~/types/search';
// export const clientLoader = async ({
//   serverLoader,
// }: ClientLoaderFunctionArgs) => {
//   return serverLoader();
// };

type LoaderData = Partial<
  Domains.TechnologiesResponse &
    Search.Site & { technologies?: Domains.Technology[] } & {
      error?: Error | AxiosError;
      breadcrumbs?: Breadcrumb[];
    }
>;

async function getAsyncData(
  request: Route.LoaderArgs['request'],
  params: Route.LoaderArgs['params'],
  isLoggedIn: boolean
): Promise<LoaderData> {
  // if (params.domain.indexOf('*') > -1) {
  //   throw data(`Invalid domain: ${params.domain}`, { status: 404 });
  // }
  try {
    parseDomain(params.domain || '');
  } catch {
    throw data(`Invalid domain: ${params.domain}`, { status: 404 });
  }

  const api = new ApiClient(request);
  const userAgent = request.headers.get('user-agent') || '';
  const isGoogleBot: boolean = /GoogleBot|GoogleOther/.test(userAgent);

  const domain = parseDomain(params.domain || '');

  if (!isLoggedIn && !isGoogleBot) {
    return {
      domain,
    };
  }

  try {
    const [details, results] = await Promise.all([
      api.get('/domains/:domain', { params: { domain } }),
      api.get('/domains/:domain/technologies', { params: { domain } }),
    ]);

    const breadcrumbs: Breadcrumb[] = [
      {
        text: 'Home',
        href: `${import.meta.env.VITE_ORIGIN}/`,
      },
      {
        text: 'Domain Lookup',
        href: `${import.meta.env.VITE_ORIGIN}/domains`,
      },
      {
        text: `Technologies used on ${details?.domain || domain}`,
        href: `${import.meta.env.VITE_ORIGIN}/domains/${domain}`,
        disabled: true,
      },
    ];

    const response: LoaderData = {
      breadcrumbs,
      ...details,
      // need to overwrite the "technologies" key
      technologies: results.domains[0]?.technologies || [],
    };

    if (response.linkedin) {
      response.linkedin = response.linkedin.split(';')[0];
    }
    if (response.twitter) {
      response.twitter = response.twitter.split(';')[0];
    }
    if (response.instagram) {
      response.instagram = response.instagram.split(';')[0];
    }
    if (response.facebook) {
      response.facebook = response.facebook.split(';')[0];
    }

    return response;
  } catch (e) {
    const error = serializeAxiosError(e as AxiosError);
    // unknown domains are queued for crawling, so show DomainNoResults instead of an error
    if ('code' in error && error.code === DOMAIN_NOT_FOUND_ERROR_CODE) {
      return {};
    }
    return {
      error,
    };
  }
}

export const loader = async ({
  context,
  request,
  params,
}: Route.LoaderArgs) => {
  return getAsyncData(request, params, await isLoggedIn(request, context));
};

export const clientLoader = async ({
  request,
  params,
}: Route.ClientLoaderArgs) => {
  return getAsyncData(
    request,
    params,
    window?.rootData?.user?.isGuest === false
  );
};

// clientLoader.hydrate = true as const;

export const meta: Route.MetaFunction = ({ loaderData, params }) => {
  const domain = ucfirst(loaderData?.domain || params.domain);
  const title = `${domain} Technology Profile ${
    loaderData?.technologies?.length
      ? ': ' + loaderData?.technologies?.length + ' Technologies In Use'
      : ''
  }`;

  const description = `See the ${
    loaderData?.technologies?.length || ''
  } technologies that ${
    domain
  } uses on their website, including ${loaderData?.technologies
    ?.slice(0, 4)
    .map((t) => t.name)
    .join(', ')}.`;

  return getPageMetaTags({
    title,
    description,
    canonical: `https://www.nerdydata.com/domains/${domain}`,
    breadcrumbs: loaderData?.breadcrumbs,
  });
};
export default function DomainPage({
  params,
  loaderData,
}: Route.ComponentProps) {
  const user = useUser();
  const suggestions = useSuggestions();
  const domain = params.domain;
  const mockDomainsData: Search.Site = getMockData();
  const [extension_installed] = useCookieStorage(
    EXTENSION_INSTALLED_COOKIE_NAME,
    ''
  );
  const mockTechnologies = useMemo(() => {
    return [...(suggestions || [])].sort(() => 0.5 - Math.random()).slice(0, 8);
  }, [suggestions]);
  const isMockData = user?.isGuest && !loaderData?.domain;

  const mockData: LoaderData | null = isMockData
    ? {
        ...mockDomainsData,
        domains: [
          {
            domain,
            technologies: mockTechnologies,
            tech_spend: {
              max: 250000,
              min: 50000,
            },
          },
        ],
      }
    : null;

  const error = deserializeAxiosError(loaderData?.error);
  const errorMessage = isAxiosError(error)
    ? error.response?.data?.message
    : error?.message;

  const isPendingRecrawl = useMemo(() => {
    if (!loaderData?.updated_at) {
      return false;
    }
    const updatedAt = new Date(loaderData.updated_at);
    const diffMonths =
      new Date().getMonth() -
      updatedAt.getMonth() +
      (new Date().getFullYear() - updatedAt.getFullYear()) * 12;
    return diffMonths >= SEARCH_FILTER_UPDATED_AT_MONTHS;
  }, [loaderData.updated_at]);

  const showErrorBanner = !!error;
  const showLoginRequiredBanner = !showErrorBanner && !!isMockData;
  const showNoResults =
    !showErrorBanner && !showLoginRequiredBanner && !loaderData?.domain;
  const showNoResultsBanner =
    !showErrorBanner &&
    !showLoginRequiredBanner &&
    !showNoResults &&
    isPendingRecrawl;
  const showExtensionBanner =
    !showErrorBanner &&
    !showNoResults &&
    !showNoResultsBanner &&
    !showLoginRequiredBanner &&
    !extension_installed;

  return (
    <PageTemplate breadcrumbs={loaderData?.breadcrumbs || []}>
      <DomainHeader
        domain={loaderData?.domain || domain}
        linkedin={loaderData?.linkedin}
        twitter={loaderData?.twitter}
        instagram={loaderData?.instagram}
        facebook={loaderData?.facebook}
        hideSearchForm={showNoResults}
      />
      {showNoResults && (
        <Grid mb={2}>
          <DomainNoResults domain={domain} />
        </Grid>
      )}
      <Grid container spacing={2} rowSpacing={4}>
        {showErrorBanner && <ErrorBanner error={errorMessage} />}
        {showNoResultsBanner && <NoResultsBanner />}
        {showExtensionBanner && <ExtensionBanner />}
      </Grid>

      {(loaderData?.domain || mockData?.domain) && (
        <>
          <Grid item xs={12}>
            <Grid container rowSpacing={2}>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h5" component="h2">
                  Website Details
                </Typography>
              </Grid>
              <Grid item xs={12}>
                {/* {isLoadingDomain && <TableSkeleton rows={2} columns={4} />} */}
                {
                  <WebsiteDetails
                    updated_at={loaderData?.updated_at || mockData?.updated_at}
                    spend_min={loaderData?.spend_min || mockData?.spend_min}
                    spend_max={loaderData?.spend_max || mockData?.spend_max}
                    industries={loaderData?.industries || mockData?.industries}
                    employees={loaderData?.employees || mockData?.employees}
                    country={loaderData?.country || mockData?.country}
                    state={loaderData?.state || mockData?.state}
                    rank={loaderData?.rank || mockData?.rank}
                  />
                }
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h5" component="h2">
                  Technologies used on {loaderData?.domain || domain}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                {/* {isLoadingDomainTechnologies && (
                    <TableSkeleton rows={2} columns={4} />
                  )} */}
                {
                  <Box sx={{ position: 'relative' }}>
                    <DomainTechnologiesTable
                      blurred={user.isGuest}
                      technologies={
                        user?.isGuest
                          ? mockTechnologies
                          : loaderData?.technologies ||
                            mockData?.technologies ||
                            []
                      }
                    />
                    {user.isGuest && (
                      <>
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            filter: 'blur(3px)',
                            opacity: 0.6,
                            userSelect: 'none',
                            pointerEvents: 'none',
                            zIndex: 1,
                          }}
                        />
                        <LoginRequiredBanner
                          domain={loaderData?.domain || domain}
                        />
                      </>
                    )}
                  </Box>
                }
              </Grid>
            </Grid>
          </Grid>
        </>
      )}
      <Grid item xs={12} my={5}>
        <Typography variant="h5">Free apps and integrations</Typography>
        <Typography variant="body1">
          Use NerdyData in other tools you use
        </Typography>
        <br />
        <IntegrationsCards />
      </Grid>
    </PageTemplate>
  );
}

interface WebsiteDetailsProps {
  updated_at?: string;
  spend_min?: number;
  spend_max?: number;
  industries?: string[];
  employees?: number;
  country?: keyof typeof countries & string;
  state?: string;
  rank?: number;
}
const WebsiteDetails = ({
  updated_at,
  spend_min,
  spend_max,
  industries,
  employees,
  country,
  state,
  rank,
}: WebsiteDetailsProps) => {
  const user = useUser();
  return (
    <Grid container spacing={1} columnGap={1} alignItems="center">
      {spend_max && spend_max > 0 && (
        <Grid item>
          <Chip
            icon={<Paid color="info" />}
            label={
              <>
                Tech Spend:{' '}
                <BlurWrapper blurred={user.isGuest}>
                  <b>
                    {`$${
                      numberAbbreviation(spend_min, 0) || '0'
                    } - $${numberAbbreviation(spend_max, 0)}`}
                  </b>
                </BlurWrapper>
              </>
            }
          />
        </Grid>
      )}
      {rank && (
        <Grid item>
          <Chip
            icon={<StarBorderIcon color="info" />}
            label={
              <>
                Rank:{' '}
                <BlurWrapper blurred={user.isGuest}>
                  <b>{getRankTier(rank)}</b>
                </BlurWrapper>
              </>
            }
          />
        </Grid>
      )}
      {employees && (
        <Grid item>
          <Chip
            icon={<BadgeIcon color="info" />}
            label={
              <>
                Employees:{' '}
                <BlurWrapper blurred={user.isGuest}>
                  <b>{employees.toLocaleString()}</b>
                </BlurWrapper>
              </>
            }
          />
        </Grid>
      )}
      {country && (
        <Grid item>
          <Chip
            icon={<LocationCityIcon color="info" />}
            label={
              <>
                HQ:{' '}
                <BlurWrapper blurred={user.isGuest}>
                  <b>{`${state ? state + ', ' : ''}${country}`}</b>
                </BlurWrapper>
              </>
            }
          />
        </Grid>
      )}
      {industries && industries.length && (
        <Grid item>
          <Chip
            icon={<ConstructionIcon color="info" />}
            label={
              <>
                Industries:{' '}
                <BlurWrapper blurred={user.isGuest}>
                  <b>{industries.join(', ')}</b>
                </BlurWrapper>
              </>
            }
          />
        </Grid>
      )}
      {updated_at && (
        <Grid item>
          <Chip
            icon={<HistoryIcon color="info" />}
            label={
              <>
                Last Crawled:{' '}
                <BlurWrapper blurred={user.isGuest}>
                  <b>{new Date(updated_at).toLocaleDateString()}</b>
                </BlurWrapper>
              </>
            }
          />
        </Grid>
      )}
    </Grid>
  );
};

interface DomainHeaderProps {
  domain: string;
  linkedin?: string;
  instagram?: string;
  twitter?: string;
  facebook?: string;
  hideSearchForm?: boolean;
}
const DomainHeader = ({
  domain,
  linkedin,
  instagram,
  twitter,
  facebook,
  hideSearchForm,
}: DomainHeaderProps) => {
  return (
    <Grid
      container
      spacing={2}
      justifyContent={'space-between'}
      alignContent={'center'}
      sx={{ mb: 8 }}>
      <Grid item>
        <Button
          variant="outlined"
          disableElevation
          href={`https://${domain}?utm_source=www.nerdydata.com`}
          startIcon={
            <Avatar variant="square" sx={{ backgroundColor: 'transparent' }}>
              <FaviconImage
                domain={domain}
                hideOnError
                height="30"
                width="30"
              />
            </Avatar>
          }
          sx={{ ml: 0 }}
          endIcon={<OpenInNewIcon />}>
          {domain}
        </Button>
      </Grid>
      <Grid item alignContent="center">
        <Stack direction="row" spacing={1}>
          {linkedin && <SocialLink type="linkedin" href={linkedin} />}
          {twitter && <SocialLink type="twitter" href={twitter} />}
          {instagram && <SocialLink type="instagram" href={instagram} />}
          {facebook && <SocialLink type="facebook" href={facebook} />}
        </Stack>
      </Grid>
      <Grid item sx={{ flexGrow: 1 }} />
      <Grid item alignContent="center">
        <Button
          href={`https://hunter.io/try/search/${domain}?via=nerdydata`}
          variant="outlined"
          target="_blank"
          color="primary"
          rel="nofollow noopener"
          className="email-hunter-button"
          endIcon={<OpenInNewIcon />}>
          Search Employee Emails
        </Button>
      </Grid>
      {!hideSearchForm && (
        <Grid item xs={12} md={4} lg={3} alignContent="center">
          <DomainSearchForm />
        </Grid>
      )}
    </Grid>
  );
};

const ExtensionBanner = () => {
  return (
    <Grid item xs={12}>
      <Alert
        severity="info"
        variant="outlined"
        action={
          <Button
            variant="outlined"
            color="inherit"
            href="/extension?utm_source=nerdydata-domain-lookup">
            Get Extension
          </Button>
        }>
        Browse websites and inspect their tech stack and spend with our Free
        Browser Extension
      </Alert>
    </Grid>
  );
};

const NoResultsBanner = () => {
  return (
    <Grid item xs={12}>
      <Alert
        severity="warning"
        variant="outlined"
        action={
          <Button
            variant="outlined"
            color="inherit"
            href="/extension?utm_source=nerdydata-domain-lookup">
            Get Extension
          </Button>
        }>
        No recent results found. We'll try to recrawl it soon, or try our{' '}
        <Link href="/extension?utm_source=no-results-found">
          Free Browser Extension
        </Link>{' '}
        for real-time results.
      </Alert>
    </Grid>
  );
};

interface LoginRequiredBannerProps {
  domain: string;
}
const LoginRequiredBanner = ({ domain }: LoginRequiredBannerProps) => {
  const benefits = [
    'See the full tech stack of any domain',
    'See company details, employees, tech spend, and more',
    'Use our free browser extension on the go',
  ];
  return (
    <Paper
      elevation={8}
      sx={{
        position: 'absolute',
        top: 40,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'calc(100% - 32px)',
        maxWidth: 472,
        p: 4,
        zIndex: 9,
      }}>
      <Stack direction="row" spacing={2} alignItems="center" mb={2}>
        <Avatar
          variant="rounded"
          sx={{
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
            color: 'primary.main',
            width: 44,
            height: 44,
          }}>
          <LockIcon />
        </Avatar>
        <Typography variant="overline" color="primary" fontWeight={700}>
          Free account
        </Typography>
      </Stack>
      <Typography variant="h5" component="h3" fontWeight={500} gutterBottom>
        See the full tech stack for {domain}
      </Typography>
      <Typography variant="body1" color="text.secondary">
        Create a free account to see every technology detected on this domain,
        grouped by category.
      </Typography>
      <List dense disablePadding sx={{ my: 2 }}>
        {benefits.map((benefit) => {
          return (
            <ListItem key={benefit} disableGutters>
              <ListItemIcon sx={{ minWidth: 30 }}>
                <CheckCircleIcon color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={benefit} />
            </ListItem>
          );
        })}
      </List>
      <Button
        variant="contained"
        size="large"
        fullWidth
        href="/signup?reason=domains.blurred">
        Sign up for free
      </Button>
      <Typography variant="body2" color="text.secondary" align="center" mt={2}>
        Already have an account? <Link href="/login">Log in</Link>
      </Typography>
    </Paper>
  );
};

const ErrorBanner = ({ error }: { error: string }) => {
  return (
    <Grid item xs={12}>
      <Alert severity="warning">
        <AlertTitle>{error || 'Something went wrong'}</AlertTitle>
        We were unable to load the results.
      </Alert>
    </Grid>
  );
};

const getMockData = (): Search.Site => ({
  company: 'Acme Corp.',
  country: 'US',
  domain: 'nerdydata.com',

  rank: 420000,
  state: 'NY',
  tld: 'com',
  first_seen_at: `2016-10-27T21:37:58.025Z`,
  url: '#',
  employees: 450,
  industries: ['Internet Technology'],
  updated_at: new Date().toISOString(),
});
